import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Calendar,
  CheckCircle2,
  Users,
  Mail,
  Phone,
  Receipt,
  ClipboardList,
  MessageSquare,
  AlertTriangle,
  TrendingUp,
  FileUp,
  StickyNote,
  Activity,
  Search,
  Filter,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { mockClientsService as clientsService } from '@/services/api/mock-clients-data';
import { cn } from '@/lib/utils';
import type { TimelineEvent, TimelineEventType } from '@/types/client';

const eventTypeConfig: Record<TimelineEventType, { label: string; icon: React.ReactNode; color: string }> = {
  document_upload: {
    label: 'Document Upload',
    icon: <FileUp className="h-4 w-4" />,
    color: 'text-blue-500',
  },
  deadline_created: {
    label: 'Deadline Created',
    icon: <Calendar className="h-4 w-4" />,
    color: 'text-purple-500',
  },
  deadline_completed: {
    label: 'Deadline Completed',
    icon: <CheckCircle2 className="h-4 w-4" />,
    color: 'text-green-500',
  },
  meeting: {
    label: 'Meeting',
    icon: <Users className="h-4 w-4" />,
    color: 'text-indigo-500',
  },
  email: {
    label: 'Email',
    icon: <Mail className="h-4 w-4" />,
    color: 'text-cyan-500',
  },
  phone_call: {
    label: 'Phone Call',
    icon: <Phone className="h-4 w-4" />,
    color: 'text-teal-500',
  },
  tax_lodgement: {
    label: 'Tax Lodgement',
    icon: <Receipt className="h-4 w-4" />,
    color: 'text-green-500',
  },
  bas_lodgement: {
    label: 'BAS Lodgement',
    icon: <ClipboardList className="h-4 w-4" />,
    color: 'text-blue-500',
  },
  note: {
    label: 'Note Added',
    icon: <StickyNote className="h-4 w-4" />,
    color: 'text-yellow-500',
  },
  status_change: {
    label: 'Status Change',
    icon: <Activity className="h-4 w-4" />,
    color: 'text-orange-500',
  },
  health_score_change: {
    label: 'Health Score Change',
    icon: <TrendingUp className="h-4 w-4" />,
    color: 'text-pink-500',
  },
  issue_detected: {
    label: 'Issue Detected',
    icon: <AlertTriangle className="h-4 w-4" />,
    color: 'text-red-500',
  },
  issue_resolved: {
    label: 'Issue Resolved',
    icon: <CheckCircle2 className="h-4 w-4" />,
    color: 'text-emerald-500',
  },
};

export function ClientTimelinePage() {
  const navigate = useNavigate();
  const [selectedClientId, setSelectedClientId] = useState<string>('all');
  const [eventTypeFilter, setEventTypeFilter] = useState<TimelineEventType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch clients for dropdown
  const { data: clientsData } = useQuery({
    queryKey: ['clients-simple'],
    queryFn: () => clientsService.listClients({ limit: 100 }),
  });

  // Fetch timeline events
  const { data: timelineData, isLoading } = useQuery({
    queryKey: ['client-timeline', selectedClientId, eventTypeFilter, searchQuery],
    queryFn: () => clientsService.getClientTimeline({
      clientId: selectedClientId !== 'all' ? selectedClientId : undefined,
      eventType: eventTypeFilter !== 'all' ? eventTypeFilter : undefined,
      search: searchQuery || undefined,
    }),
  });

  const events = timelineData?.events || [];
  const clients = clientsData?.clients || [];

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return diffMins === 0 ? 'Just now' : `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      });
    }
  };

  const formatFullTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Group events by date
  const groupedEvents = events.reduce((acc, event) => {
    const date = new Date(event.timestamp).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(event);
    return acc;
  }, {} as Record<string, TimelineEvent[]>);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Client Timeline</h1>
          <p className="text-muted-foreground">
            Chronological view of all client interactions and events
          </p>
        </div>
        <Badge variant="outline" className="text-sm">
          {timelineData?.total || 0} events
        </Badge>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <Select value={eventTypeFilter} onValueChange={(v) => setEventTypeFilter(v as TimelineEventType | 'all')}>
                <SelectTrigger>
                  <SelectValue placeholder="Event type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Event Types</SelectItem>
                  {Object.entries(eventTypeConfig).map(([type, config]) => (
                    <SelectItem key={type} value={type}>
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search events..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Event Timeline</CardTitle>
          <CardDescription>
            {selectedClientId !== 'all'
              ? `Showing events for ${clients.find(c => c.id === selectedClientId)?.displayName}`
              : 'Showing events across all clients'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="font-medium">No events found</p>
              <p className="text-sm text-muted-foreground mt-1">
                {searchQuery || eventTypeFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Events will appear here as they occur'}
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[600px] pr-4">
              <div className="space-y-6">
                {Object.entries(groupedEvents).map(([date, dateEvents]) => (
                  <div key={date}>
                    <h3 className="text-sm font-semibold text-muted-foreground mb-3 sticky top-0 bg-background py-2">
                      {date}
                    </h3>
                    <div className="space-y-3 relative pl-6">
                      {/* Timeline line */}
                      <div className="absolute left-2 top-0 bottom-0 w-px bg-border" />

                      {dateEvents.map((event, index) => {
                        const config = eventTypeConfig[event.eventType];
                        return (
                          <div key={event.id} className="relative">
                            {/* Timeline dot */}
                            <div className={cn(
                              'absolute -left-6 top-2 h-4 w-4 rounded-full border-4 border-background bg-background flex items-center justify-center',
                              config.color
                            )}>
                              <div className={cn('h-2 w-2 rounded-full', config.color.replace('text-', 'bg-'))} />
                            </div>

                            <div
                              className="p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors cursor-pointer"
                              onClick={() => {
                                if (event.clientId) {
                                  navigate(`/panel/files/clients/${event.clientId}`);
                                }
                              }}
                            >
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <div className={config.color}>
                                      {config.icon}
                                    </div>
                                    <h4 className="font-medium text-sm">{event.title}</h4>
                                    <Badge variant="outline" className="text-xs">
                                      {config.label}
                                    </Badge>
                                  </div>
                                  {event.description && (
                                    <p className="text-sm text-muted-foreground mt-1">
                                      {event.description}
                                    </p>
                                  )}
                                  <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                    <span title={formatFullTimestamp(event.timestamp)}>
                                      {formatTimestamp(event.timestamp)}
                                    </span>
                                    {event.performedBy && (
                                      <>
                                        <span>•</span>
                                        <span>by {event.performedBy}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
